import * as z from 'zod'
import axios, { type AxiosRequestConfig } from 'axios'
import { siteOrigin } from '~/constants/routing'
import type { GetR2SignedUrlParams } from '~/app/api/repo/get-r2-signed-url/schema'
import { getPlaygroundIdCookie } from '@basehub/data/replicache/playground/utils'

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export const uploadFileToRepo = async (
  file: File,
  {
    repoId,
    teamId,
    onUploadProgress,
    fileHash,
    protectFile,
  }: {
    repoId: string
    teamId: string
    onUploadProgress?: AxiosRequestConfig<File>['onUploadProgress']
    fileHash: string
    protectFile?: boolean
  }
) => {
  if (file.size > 5 * 1024 * 1024) {
    return await uploadLargeFileToRepo(file, {
      repoId,
      teamId,
      fileHash,
      protectFile,
    })
  }

  const res = await fetch(new URL('/api/repo/get-r2-signed-url', siteOrigin), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      repoId,
      teamId,
      fileName: file.name,
      contentType: file.type,
      contentLength: file.size,
      fileHash,
      protectFile,
      playgroundId: getPlaygroundIdCookie(),
    } satisfies GetR2SignedUrlParams),
  })
  const json = await res.json()
  const parsed = z
    .object({
      signedURL: z.string(),
      uploadURL: z.string(),
      isExisting: z.boolean(),
    })
    .parse(json)

  if (!parsed.isExisting) {
    const blob = new Blob([await file.arrayBuffer()], { type: file.type })
    await axios.put(parsed.signedURL, blob, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress,
    })
  }

  return { uploadURL: parsed.uploadURL }
}

// Multipart upload implementation
const uploadLargeFileToRepo = async (
  file: File,
  {
    repoId,
    teamId,
    onUploadProgress,
    fileHash,
    chunkSize = 25 * 1024 * 1024, // 25MB chunks by default (R2 minimum is 5MB)
    enableResume = true, // Enable resumable uploads by default
    protectFile,
  }: {
    repoId: string
    teamId: string
    onUploadProgress?: (progress: number) => void
    fileHash: string
    chunkSize?: number
    enableResume?: boolean
    protectFile: boolean | undefined
  }
) => {
  // Create a unique identifier for this upload
  const uploadKey = `upload:${repoId}:${file.name}:${fileHash}:${file.size}`

  // Define the resume data type
  type ResumeData = {
    uploadId: string
    key: string
    uploadedParts: { etag: string; partNumber: number }[]
    signedURL: string
    uploadURL: string
  }

  // Check if we have a saved upload state
  let resumeData: ResumeData | null = null

  if (enableResume) {
    try {
      const savedData = localStorage.getItem(uploadKey)
      if (savedData) {
        // Use zod to validate the structure
        const parsedData = JSON.parse(savedData)
        const resumeSchema = z.object({
          uploadId: z.string(),
          key: z.string(),
          signedURL: z.string(),
          uploadURL: z.string(),
          uploadedParts: z.array(
            z.object({
              etag: z.string(),
              partNumber: z.number(),
            })
          ),
        })

        const validationResult = resumeSchema.safeParse(parsedData)
        if (validationResult.success) {
          resumeData = validationResult.data
          console.log('Resuming previous upload session', resumeData)
        } else {
          console.warn('Invalid resume data format', validationResult.error)
        }
      }
    } catch (error) {
      console.warn('Failed to parse resume data', error)
      // Continue with a fresh upload
    }
  }

  // If we're not resuming, start a new upload
  if (!resumeData) {
    // Step 1: Get the signed URL for multipart upload
    const res = await fetch(
      new URL('/api/repo/get-r2-signed-url', siteOrigin),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          repoId,
          teamId,
          fileName: file.name,
          fileHash,
          type: 'multipart',
          contentType: file.type,
          contentLength: file.size,
          protectFile,
        } satisfies GetR2SignedUrlParams),
      }
    )

    const json = await res.json()
    const parsed = z
      .object({
        signedURL: z.string(),
        uploadURL: z.string(),
        isExisting: z.boolean(),
      })
      .parse(json)

    // If file already exists, return early
    if (parsed.isExisting) {
      // Clean up any saved state
      if (enableResume) {
        localStorage.removeItem(uploadKey)
      }
      return { uploadURL: parsed.uploadURL }
    }

    // Step 2: Initialize multipart upload
    const initResponse = await fetch(parsed.signedURL, {
      method: 'POST',
    })

    if (!initResponse.ok) {
      throw new Error(
        `Failed to initialize multipart upload: ${await initResponse.text()}`
      )
    }

    // Define the expected response type
    const initData = await initResponse.json()
    const { uploadId, key } = z
      .object({
        uploadId: z.string(),
        key: z.string(),
      })
      .parse(initData)

    // Initialize resume data
    resumeData = {
      uploadId,
      key,
      uploadedParts: [],
      signedURL: parsed.signedURL,
      uploadURL: parsed.uploadURL,
    }

    // Save initial state if resume is enabled
    if (enableResume) {
      localStorage.setItem(uploadKey, JSON.stringify(resumeData))
    }
  }

  try {
    const {
      uploadId,
      key,
      uploadedParts: existingParts,
      signedURL,
      uploadURL,
    } = resumeData
    const uploadedParts = [...existingParts] // Copy existing parts

    // Step 3: Upload parts in chunks
    const totalChunks = Math.ceil(file.size / chunkSize)

    // Find the next part to upload (skip already uploaded parts)
    const uploadedPartNumbers = new Set(uploadedParts.map((p) => p.partNumber))

    // Upload remaining parts
    for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
      // Skip already uploaded parts
      if (uploadedPartNumbers.has(partNumber)) {
        // Report progress for already uploaded parts
        if (onUploadProgress) {
          onUploadProgress((uploadedParts.length / totalChunks) * 100)
        }
        continue
      }

      const start = (partNumber - 1) * chunkSize
      const end = Math.min(partNumber * chunkSize, file.size)
      const chunk = file.slice(start, end)

      // Upload this chunk
      const partResponse = await fetch(
        `${signedURL}&uploadId=${uploadId}&partNumber=${partNumber}`,
        {
          method: 'PUT',
          body: chunk,
        }
      )

      if (!partResponse.ok) {
        throw new Error(
          `Failed to upload part ${partNumber}: ${await partResponse.text()}`
        )
      }

      const partResultRaw = await partResponse.json()
      const partResult = z
        .object({
          etag: z.string(),
          partNumber: z.number(),
        })
        .parse(partResultRaw)

      uploadedParts.push({
        etag: partResult.etag,
        partNumber: partResult.partNumber,
      })

      // Save progress after each part if resume is enabled
      if (enableResume) {
        localStorage.setItem(
          uploadKey,
          JSON.stringify({
            uploadId,
            key,
            uploadedParts,
            signedURL,
            uploadURL,
          })
        )
      }

      // Report progress
      if (onUploadProgress) {
        onUploadProgress((uploadedParts.length / totalChunks) * 100)
      }
    }

    // Step 4: Complete the multipart upload
    const completeResponse = await fetch(`${signedURL}&uploadId=${uploadId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ parts: uploadedParts }),
    })

    if (!completeResponse.ok) {
      throw new Error(
        `Failed to complete multipart upload: ${await completeResponse.text()}`
      )
    }

    // Clean up resume data after successful upload
    if (enableResume) {
      localStorage.removeItem(uploadKey)
    }

    return { uploadURL }
  } catch (error) {
    // Don't clean up resume data on error - this allows for resuming later

    // If we're not enabling resume, abort the multipart upload to clean up
    if (!enableResume) {
      await fetch(`${resumeData.signedURL}&uploadId=${resumeData.uploadId}`, {
        method: 'DELETE',
      }).catch(console.error) // Ignore errors in cleanup
    }

    throw error
  }
}
